import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaPhoneAlt, FaSearch, FaSync } from 'react-icons/fa';
import {
  outcomeChips,
  callCategory,
  formatCallDay,
  formatDuration,
  formatRelative,
  callerInitials,
} from '../utils/voiceFormat';
import './VoiceTickets.css';

const PAGE_SIZE = 50;

const VoiceTickets = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  // No default window: a tenant's calls may all be months old (pilot lines),
  // and an empty page reads as broken. The date filter is opt-in.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [line, setLine] = useState('');
  const [facet, setFacet] = useState('all');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchConversations = useCallback(async (skip = 0) => {
    const setBusy = skip > 0 ? setLoadingMore : setLoading;
    setBusy(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('limit', String(PAGE_SIZE));
      if (skip > 0) params.append('skip', String(skip));
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await axios.get(`/api/ai-voice/conversations?${params.toString()}`, { headers });
      const rows = response.data?.conversations || [];
      setTotal(response.data?.total ?? rows.length);
      setConversations((prev) => (skip > 0 ? [...prev, ...rows] : rows));
    } catch (err) {
      console.error('Failed to fetch voice conversations:', err);
      setError(err?.response?.data?.detail || t('voiceTickets.fetchError', 'Échec du chargement des conversations vocales.'));
      if (skip === 0) setConversations([]);
    } finally {
      setBusy(false);
    }
  }, [headers, startDate, endDate, t]);

  useEffect(() => {
    fetchConversations(0);
  }, [fetchConversations]);

  // The phone lines present in the loaded calls (a line = a voice agent).
  const lines = useMemo(() => {
    const byNumber = new Map();
    conversations.forEach((conv) => {
      if (conv.dialed_number && !byNumber.has(conv.dialed_number)) {
        byNumber.set(conv.dialed_number, conv.assistant_name || conv.dialed_number);
      }
    });
    return Array.from(byNumber, ([number, label]) => ({ number, label }));
  }, [conversations]);

  const searched = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return conversations.filter((conv) => {
      if (line && conv.dialed_number !== line) return false;
      if (!needle) return true;
      return [conv.caller_name, conv.phone_number, conv.preview]
        .some((field) => (field || '').toLowerCase().includes(needle));
    });
  }, [conversations, query, line]);

  const facetCounts = useMemo(() => {
    const counts = { all: searched.length, action: 0, escalated: 0, info: 0 };
    searched.forEach((conv) => { counts[callCategory(conv.tool_names)] += 1; });
    return counts;
  }, [searched]);

  const visible = useMemo(() => (
    facet === 'all' ? searched : searched.filter((conv) => callCategory(conv.tool_names) === facet)
  ), [searched, facet]);

  const FACETS = [
    { key: 'all', label: t('voiceTickets.facetAll', 'Tous') },
    { key: 'action', label: t('voiceTickets.facetAction', 'Avec action') },
    { key: 'escalated', label: t('voiceTickets.facetEscalated', 'Escaladés') },
    { key: 'info', label: t('voiceTickets.facetInfo', 'Info seule') },
  ];

  const openCall = (callId) => navigate(`/voice-tickets/${encodeURIComponent(callId)}`);

  return (
    <div className="vt-page">
      <header className="vt-pagehead">
        <div className="vt-pagehead-title">
          <span className="vt-icon-tile"><FaPhoneAlt /></span>
          <div>
            <h1>{t('voiceTickets.title', 'Tickets vocaux')}</h1>
            <p>{t('voiceTickets.subtitle', 'Les conversations téléphoniques traitées par vos agents vocaux.')}</p>
          </div>
        </div>
        <button type="button" className="vt-btn" onClick={() => fetchConversations(0)} disabled={loading}>
          <FaSync className={loading ? 'fa-spin' : undefined} />
          {t('voiceTickets.refresh', 'Actualiser')}
        </button>
      </header>

      <div className="vt-toolbar">
        <div className="vt-search">
          <FaSearch aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('voiceTickets.searchPlaceholder', 'Rechercher un nom ou un numéro…')}
            aria-label={t('voiceTickets.search', 'Rechercher')}
          />
        </div>
        <div className="vt-dates">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            max={endDate || undefined}
            aria-label={t('voiceTickets.startDate', 'Début')}
          />
          <span>→</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate || undefined}
            aria-label={t('voiceTickets.endDate', 'Fin')}
          />
        </div>
        {lines.length > 1 && (
          <select
            className="vt-select"
            value={line}
            onChange={(e) => setLine(e.target.value)}
            aria-label={t('voiceTickets.line', 'Ligne')}
          >
            <option value="">{t('voiceTickets.allLines', 'Toutes les lignes')}</option>
            {lines.map((item) => (
              <option key={item.number} value={item.number}>{item.label}</option>
            ))}
          </select>
        )}
      </div>

      <div className="vt-facets" role="group" aria-label={t('voiceTickets.facets', 'Type d’appel')}>
        {FACETS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`vt-facet${facet === item.key ? ' active' : ''}`}
            aria-pressed={facet === item.key}
            onClick={() => setFacet(item.key)}
          >
            {item.label} <small>{facetCounts[item.key]}</small>
          </button>
        ))}
      </div>

      {error && <div className="vt-alert">{error}</div>}

      <div className="vt-calls">
        {loading ? (
          <div className="vt-state">{t('voiceTickets.loading', 'Chargement des conversations…')}</div>
        ) : visible.length === 0 ? (
          <div className="vt-state">{t('voiceTickets.empty', 'Aucune conversation vocale trouvée.')}</div>
        ) : (
          visible.map((conv) => {
            const chips = outcomeChips(conv.tool_names, t);
            const lineLabel = conv.assistant_name || conv.dialed_number;
            return (
              <div
                key={conv.call_id}
                className="vt-call"
                role="button"
                tabIndex={0}
                onClick={() => openCall(conv.call_id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCall(conv.call_id); }
                }}
              >
                <span className={`vt-avatar${conv.caller_name ? '' : ' unknown'}`} aria-hidden="true">
                  {callerInitials(conv.caller_name, conv.phone_number)}
                </span>
                <div className="vt-who">
                  {conv.caller_name ? (
                    <>
                      <b>{conv.caller_name}</b>
                      <span>{conv.phone_number || '—'}</span>
                    </>
                  ) : (
                    <>
                      <b className="vt-num">{conv.phone_number || t('voiceTickets.detail.unknownCaller', 'Appelant inconnu')}</b>
                      <span>{t('voiceTickets.unknownCallerSub', 'appelant inconnu')}</span>
                    </>
                  )}
                </div>
                <div className="vt-what">
                  <div className="vt-chips">
                    {chips.slice(0, 3).map((chip) => (
                      <span key={chip.name} className={`vt-chip ${chip.kind}`}>{chip.label}</span>
                    ))}
                    {chips.length === 0 && (
                      <span className="vt-chip info">{t('voiceTickets.facetInfo', 'Info seule')}</span>
                    )}
                    {lineLabel && <span className="vt-chip line">{lineLabel}</span>}
                  </div>
                  {conv.preview && <div className="vt-preview">« {conv.preview} »</div>}
                </div>
                <div className="vt-when">
                  <b>{formatCallDay(conv.started_at)}</b>
                  <span>{formatRelative(conv.started_at, t)}</span>
                </div>
                <div className="vt-stats">
                  <span>{formatDuration(conv.duration_seconds)}</span>
                  <span>{t('voiceTickets.turnsCount', '{{n}} échanges', { n: conv.turn_count ?? 0 })}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="vt-listfoot">
        <span>
          {(startDate || endDate)
            ? t('voiceTickets.totalCalls', '{{n}} appels sur la période', { n: total })
            : t('voiceTickets.totalCallsAll', '{{n}} appels', { n: total })}
        </span>
        {conversations.length < total && (
          <button type="button" className="vt-btn" onClick={() => fetchConversations(conversations.length)} disabled={loadingMore}>
            {loadingMore ? t('voiceTickets.loadingMore', 'Chargement…') : t('voiceTickets.loadMore', 'Charger plus')}
          </button>
        )}
      </div>
    </div>
  );
};

export default VoiceTickets;
